import { createTheme } from '@mui/material/styles';
import { AIRMASK_GREEN, AIRMASK_DARK } from './constants/colors';

// Option A: Professional/Corporate Typography Theme with Green Accent
export const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: AIRMASK_GREEN, // Keep the existing green theme for health/habits
      contrastText: AIRMASK_DARK,
    },
    secondary: {
      main: '#FF6B35',
      contrastText: '#ffffff',
    },
    background: {
      default: AIRMASK_DARK,
      paper: AIRMASK_DARK,
    },
    text: {
      primary: '#ffffff',
      secondary: '#b0b0b0',
    },
    grey: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#eeeeee',
      300: '#e0e0e0',
      400: '#bdbdbd',
      500: '#9e9e9e',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
    },
    divider: '#666666',
  },
  typography: {
    // Headers use Oswald for chunky, serious feel
    h1: {
      fontFamily: '"Oswald", sans-serif',
      fontWeight: 700,
      fontSize: '2.5rem',
      letterSpacing: '1px',
      color: '#ffffff',
    },
    h2: {
      fontFamily: '"Oswald", sans-serif',
      fontWeight: 600,
      fontSize: '2rem',
      letterSpacing: '1px',
      color: '#ffffff',
    },
    h3: {
      fontFamily: '"Oswald", sans-serif',
      fontWeight: 600,
      fontSize: '1.5rem',
      letterSpacing: '1px',
      color: '#ffffff',
    },
    h4: {
      fontFamily: '"Oswald", sans-serif',
      fontWeight: 600, // Keep existing weight but add Oswald
      fontSize: '1.25rem',
      letterSpacing: '0.5px',
      color: '#ffffff',
    },
    h5: {
      fontFamily: '"Oswald", sans-serif',
      fontWeight: 500,
      fontSize: '1.125rem',
      letterSpacing: '0.5px',
      color: '#ffffff',
    },
    h6: {
      fontFamily: '"Oswald", sans-serif',
      fontWeight: 500,
      fontSize: '1rem',
      letterSpacing: '0.5px',
      color: '#ffffff',
    },
    // Body text uses Inter with heavier weights for serious, chunky appearance
    body1: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 500,
      fontSize: '1rem',
      lineHeight: 1.7,
      color: '#ffffff',
    },
    body2: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 500,
      fontSize: '0.875rem',
      lineHeight: 1.6,
      color: '#b0b0b0',
    },
    // Buttons with chunky text
    button: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 600,
      fontSize: '0.875rem',
      letterSpacing: '0.5px',
      textTransform: 'uppercase',
    },
    // Captions and smaller text
    caption: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 500,
      fontSize: '0.75rem',
      color: '#999999',
    },
    // Overline for labels
    overline: {
      fontFamily: '"Oswald", sans-serif',
      fontWeight: 600,
      fontSize: '0.75rem',
      letterSpacing: '1px',
      textTransform: 'uppercase',
      color: '#b0b0b0',
    },
  },
  components: {
    // Global styles for consistent checkbox coloring
    MuiCssBaseline: {
      styleOverrides: {
        // Style native HTML checkboxes (from markdown) to match Material-UI theme
        'input[type="checkbox"]': {
          accentColor: AIRMASK_GREEN, // Match primary green color
          cursor: 'pointer',
        },
      },
    },
    // Keep existing green color scheme but update with new typography
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          fontFamily: '"Inter", sans-serif',
          fontWeight: 500,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: AIRMASK_GREEN,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: AIRMASK_GREEN,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: AIRMASK_GREEN,
          },
        },
        input: {
          fontFamily: '"Inter", sans-serif',
          fontWeight: 500,
          color: '#ffffff',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontFamily: '"Inter", sans-serif',
          fontWeight: 500,
          color: '#b0b0b0',
          '&.Mui-focused': {
            color: AIRMASK_GREEN,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          fontFamily: '"Inter", sans-serif',
          fontWeight: 500,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: AIRMASK_GREEN,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: AIRMASK_GREEN,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: AIRMASK_GREEN,
          },
        },
        select: {
          fontFamily: '"Inter", sans-serif',
          fontWeight: 500,
          color: '#ffffff',
        },
        icon: {
          color: '#888888', // Set dropdown arrow icon color to gray
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: '"Inter", sans-serif',
          fontWeight: 500,
          color: '#ffffff',
          backgroundColor: '#222222',
          '&:hover': {
            backgroundColor: '#333333',
          },
          '&.Mui-selected': {
            backgroundColor: AIRMASK_GREEN,
            color: AIRMASK_DARK,
            fontWeight: 600,
            '&:hover': {
              backgroundColor: '#2BA519',
            },
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: '"Inter", sans-serif',
          fontWeight: 600,
          color: '#888888', // Default color for tabs not in NavBar
          '&.Mui-selected': {
            color: AIRMASK_GREEN, // Selected color for tabs not in NavBar
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: AIRMASK_GREEN,
        },
        // Override for tabs inside AppBar (NavBar)
        root: {
          '&.MuiAppBar-root &': {
            '& .MuiTab-root': {
              color: AIRMASK_DARK,
              '&.Mui-selected': {
                color: AIRMASK_DARK,
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: AIRMASK_DARK,
            },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: AIRMASK_GREEN,
          color: AIRMASK_DARK, // Keep original dark text color for NavBar
          '& .MuiTypography-root': {
            color: AIRMASK_DARK, // Ensure all typography in AppBar is dark
          },
          '& .MuiTab-root': {
            color: AIRMASK_DARK, // Dark text for tabs in NavBar
            '&.Mui-selected': {
              color: AIRMASK_DARK, // Keep dark even when selected
              fontWeight: 700,
            },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: '"Inter", sans-serif',
          fontWeight: 600,
          letterSpacing: '0.5px',
        },
        // Style for primary buttons (green background)
        containedPrimary: {
          backgroundColor: AIRMASK_GREEN,
          color: AIRMASK_DARK,
          '& .MuiSvgIcon-root': {
            color: AIRMASK_DARK, // Dark icons on green buttons
          },
          '&:hover': {
            backgroundColor: '#2BA519',
            '& .MuiSvgIcon-root': {
              color: AIRMASK_DARK, // Keep dark icons on hover
            },
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-root': {
            fontFamily: '"Inter", sans-serif',
            fontWeight: 500,
            color: '#ffffff',
          },
          '& .MuiInputLabel-root': {
            fontFamily: '"Inter", sans-serif',
            fontWeight: 500,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#222222',
          color: '#ffffff',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#222222',
          color: '#ffffff',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: '"Inter", sans-serif',
          fontWeight: 600,
          fontSize: '0.75rem',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: '#ffffff',
        },
      },
    },
  },
});